/**
 * Print Estimation Calculator
 *
 * Converts STL analysis (volume, dimensions) into:
 * - Filament weight (grams)
 * - Print time (hours)
 * - Confidence level
 *
 * Calibrated for Bambu Lab P1 with PLA filament
 */

import type { STLAnalysis } from './stlParser';

export interface PrintEstimate {
  filamentGrams: number;
  printTimeHours: number;
  confidence: 'high' | 'medium' | 'low';
  warnings: string[];
  source: 'stl' | 'url' | 'manual';
}

/**
 * Constants for Bambu Lab P1 with PLA
 */
const CONSTANTS = {
  // Material properties
  PLA_DENSITY: 1.24, // g/cm³

  // Print settings (conservative defaults)
  INFILL_PERCENTAGE: 0.15, // 15% infill
  WALL_THICKNESS_MM: 1.2, // 3 perimeters @ 0.4mm nozzle
  LAYER_HEIGHT_MM: 0.2, // Standard quality
  TOP_BOTTOM_LAYERS: 4, // Solid top/bottom

  // Empirical filament usage ratio
  // Research shows prints typically use 25-40% of bounding box volume
  VOLUME_TO_FILAMENT_RATIO: 0.35, // 35% of model volume becomes filament

  // Safety margins (to prevent losses)
  FILAMENT_SAFETY_MARGIN: 1.15, // 15% buffer for supports/waste/purge
  TIME_SAFETY_MARGIN: 1.2, // 20% buffer for slowdowns/retractions

  // Model size limits (Bambu P1)
  MAX_DIMENSIONS_CM: { x: 25.6, y: 25.6, z: 25.6 }, // 256mm build volume
  MIN_VOLUME_CM3: 0.5, // Minimum practical print
  MAX_VOLUME_CM3: 2000, // Prevent massive estimates
};

/**
 * Estimate print time and filament from STL analysis
 */
export function estimatePrint(analysis: STLAnalysis): PrintEstimate {
  const { volumeCm3, boundingBox, surfaceAreaCm2 } = analysis;
  const C = CONSTANTS;

  const warnings: string[] = [];
  let confidence: 'high' | 'medium' | 'low' = 'medium';

  // Validate dimensions
  if (
    boundingBox.x > C.MAX_DIMENSIONS_CM.x ||
    boundingBox.y > C.MAX_DIMENSIONS_CM.y ||
    boundingBox.z > C.MAX_DIMENSIONS_CM.z
  ) {
    warnings.push(`Model exceeds printer build volume (${C.MAX_DIMENSIONS_CM.x}cm)`);
    confidence = 'low';
  }

  if (volumeCm3 < C.MIN_VOLUME_CM3) {
    warnings.push('Very small model - estimate may be less accurate');
    confidence = 'low';
  }

  if (volumeCm3 > C.MAX_VOLUME_CM3) {
    warnings.push('Very large model - verify estimate before ordering');
    confidence = 'low';
  }

  // Simplified filament calculation using empirical ratio
  // Most prints with 15% infill use ~30-40% of the solid model volume
  // This accounts for: hollow interior, sparse infill, perimeter walls, top/bottom
  const printVolumeCm3 = volumeCm3 * C.VOLUME_TO_FILAMENT_RATIO;

  // Convert to filament weight
  const baseFilamentGrams = printVolumeCm3 * C.PLA_DENSITY;

  // Apply safety margin for waste, supports, purge
  const filamentGrams = baseFilamentGrams * C.FILAMENT_SAFETY_MARGIN;

  // Estimate print time using empirical grams/hour rate
  // Research: Bambu Lab P1 (CoreXY) averages 20-25 g/hr
  // Source: https://3dprintmentor.com/how-much-filament-is-used-in-3d-printing/
  const GRAMS_PER_HOUR_BASE = 22; // Conservative for Bambu P1

  // Adjust rate based on model characteristics
  let gramsPerHour = GRAMS_PER_HOUR_BASE;

  // Tall thin prints are slower (more travel, layer changes)
  const aspectRatio = boundingBox.z / Math.max(boundingBox.x, boundingBox.y);
  if (aspectRatio > 2) {
    gramsPerHour *= 0.8; // 20% slower for tall prints
  }

  // Very small prints are slower (precision movements)
  if (volumeCm3 < 10) {
    gramsPerHour *= 0.85;
  }

  // Base time from filament weight
  const baseTimeHours = filamentGrams / gramsPerHour;

  // Add overhead for heating, homing, first layer
  const overheadHours = 0.15; // ~9 minutes startup/finish

  // Apply safety margin
  const printTimeHours = (baseTimeHours + overheadHours) * C.TIME_SAFETY_MARGIN;

  // Adjust confidence based on model characteristics
  if (boundingBox.z > 15) {
    warnings.push('Tall model - may need supports (adds time/filament)');
  }

  if (volumeCm3 > 100 && volumeCm3 < 500) {
    confidence = 'high'; // Medium-sized prints are most predictable
  }

  // Warn about very long prints
  if (printTimeHours > 12) {
    warnings.push(`Long print time (${Math.round(printTimeHours)}h) - verify settings`);
  }

  return {
    filamentGrams: Math.round(filamentGrams * 10) / 10, // Round to 0.1g
    printTimeHours: Math.round(printTimeHours * 10) / 10, // Round to 0.1h
    confidence,
    warnings,
    source: 'stl',
  };
}

/**
 * Calculate price from estimate (uses existing pricing formula)
 */
export function calculatePriceFromEstimate(estimate: PrintEstimate): number {
  const KWH_RATE = 0.48; // California electricity
  const PRINTER_WATTS = 150; // Bambu P1
  const COST_PER_GRAM = 0.03; // Filament cost ($30/kg spool)
  const MARKUP_MULTIPLIER = 1.5; // Business markup

  const electricityCost = ((estimate.printTimeHours * PRINTER_WATTS) / 1000) * KWH_RATE;
  const filamentCost = estimate.filamentGrams * COST_PER_GRAM;
  const totalCost = (electricityCost + filamentCost) * MARKUP_MULTIPLIER;

  // Always round UP to nearest $0.50 to prevent losses
  return Math.ceil(totalCost * 2) / 2;
}
